import * as React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
  Alert,
  Chip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate, useParams } from 'react-router';
import dayjs from 'dayjs';
import { useDialogs } from '../hooks/useDialogs/useDialogs';
import useNotifications from '../hooks/useNotifications/useNotifications';
import { getJobById, deleteJobById } from '../../services/jobService';
import { applyToJob } from '../../services/applicationService'; // ✅ added
import PageContainer from './PageContainer';
import { useAuth } from '../../auth/AuthContext';

export default function JobShow() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const dialogs = useDialogs();
  const notifications = useNotifications();

  const [job, setJob] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [isApplying, setIsApplying] = React.useState(false);
  const [applied, setApplied] = React.useState(false);

  const loadData = React.useCallback(async () => {
    setError(null);
    setIsLoading(true);

    try {
      const response = await getJobById(Number(id));

      if (response.success === false && response.cause === "Unauthorized access") {
        throw new Error("You are not authorized to view this job.");
      }

      setJob(response.message);
    } catch (err) {
      setError(err);
    }
    setIsLoading(false);
  }, [id]);

  React.useEffect(() => {
    loadData();
  }, [loadData]);

  const handleJobEdit = () => navigate(`/app/hr/job/${id}/edit`);

  const handleJobDelete = async () => {
    if (!job) return;
    const confirmed = await dialogs.confirm(
      `Do you wish to delete "${job.jobTitle}"?`,
      {
        title: `Delete job?`,
        severity: 'error',
        okText: 'Delete',
        cancelText: 'Cancel',
      }
    );
    if (confirmed) {
      setIsLoading(true);
      try {
        await deleteJobById(Number(id));
        navigate('/app/jobs');
        notifications.show('Job deleted successfully.', { severity: 'success',autoHideDuration: 3000});
      } catch (deleteError) {
        notifications.show(`Failed to delete job. ${deleteError.message}`, {
          severity: 'error',
          autoHideDuration: 3000
        });
      }
      setIsLoading(false);
    }
  };

  const handleApply = async () => {
  if (!job || applied) return;
  setIsApplying(true);

  try {
    const response = await applyToJob(job.id);
    console.log("Apply response:", response);

    if (response.success) {
      setApplied(true);
      notifications.show("Applied successfully!", {
        severity: "success",
        autoHideDuration: 3000,
      });
    } else {
      // Handle API failure gracefully
      notifications.show(response.message || "Failed to apply", {
        severity: "error",
        autoHideDuration: 3000,
      });
    }
  } catch (err) {
    notifications.show(`Failed to apply: ${err.message}`, {
      severity: "error",
      autoHideDuration: 3000,
    });
  } finally {
    setIsApplying(false);
  }
};

  const handleBack = () => navigate('/app/jobs');

  if (isLoading) {
    return (
      <PageContainer title={`Job ${id}`}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
          <CircularProgress />
        </Box>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer title={`Job ${id}`}>
        <Alert severity="error">{error.message}</Alert>
      </PageContainer>
    );
  }

  return (
    <PageContainer
      title={job.jobTitle}
      breadcrumbs={[
        { title: 'Jobs', path: '/app/jobs' },
        { title: job.jobTitle },
      ]}
    >
      <Stack spacing={3} sx={{ width: '100%' }}>
        {/* Top Info */}
        <Paper sx={{ p: 3 }} elevation={2}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Typography variant="overline">Department</Typography>
              <Typography variant="body1">{job.department}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="overline">Location</Typography>
              <Typography variant="body1">{job.location}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="overline">Employment Type</Typography>
              <Typography variant="body1">{job.employmentType}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="overline">Experience</Typography>
              <Typography variant="body1">{job.experienceLevel}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="overline">Education</Typography>
              <Typography variant="body1">{job.educationRequirements}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="overline">Posted On</Typography>
              <Typography variant="body1">
                {dayjs(job.createdAt).format('MMMM D, YYYY')}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="overline">Salary Range</Typography>
              <Typography variant="h6" color="primary">
                {job.minSalary?.toLocaleString()} –{' '}
                {job.maxSalary?.toLocaleString()} {job.currency}
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        {/* Summary */}
        <Paper sx={{ p: 3 }} elevation={2}>
          <Typography variant="overline">Summary</Typography>
          <Typography variant="body1">{job.jobSummary}</Typography>
        </Paper>

        {/* Responsibilities */}
        {job.responsibilities?.length > 0 && (
          <Paper sx={{ p: 3 }} elevation={2}>
            <Typography variant="overline">Responsibilities</Typography>
            <Stack spacing={1} mt={1}>
              {job.responsibilities.map((res, idx) => (
                <Typography key={idx} variant="body2">
                  • {res}
                </Typography>
              ))}
            </Stack>
          </Paper>
        )}

        {/* Skills */}
        <Grid container spacing={3}>
          {job.requiredSkills?.length > 0 && (
            <Grid item xs={12} sm={6}>
              <Paper sx={{ p: 3 }} elevation={2}>
                <Typography variant="overline">Required Skills</Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" mt={1}>
                  {job.requiredSkills.map((skill, idx) => (
                    <Chip key={idx} label={skill} color="primary" variant="outlined" />
                  ))}
                </Stack>
              </Paper>
            </Grid>
          )}
          {job.preferredSkills?.length > 0 && (
            <Grid item xs={12} sm={6}>
              <Paper sx={{ p: 3 }} elevation={2}>
                <Typography variant="overline">Preferred Skills</Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" mt={1}>
                  {job.preferredSkills.map((skill, idx) => (
                    <Chip key={idx} label={skill} color="secondary" variant="outlined" />
                  ))}
                </Stack>
              </Paper>
            </Grid>
          )}
        </Grid>

        {/* Actions */}
        <Divider />
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
          >
            Back
          </Button>
          {user?.role === "HR" ? (
            <>
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={handleJobEdit}
              >
                Edit
              </Button>
              <Button
                variant="contained"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleJobDelete}
              >
                Delete
              </Button>
            </>
          ) : (
            <Button
              variant="contained"
              color={applied ? "success" : "primary"}
              onClick={handleApply}
              disabled={isApplying || applied}
            >
              {applied ? "Applied" : isApplying ? "Applying..." : "Apply"}
            </Button>
          )}
        </Stack>
      </Stack>
    </PageContainer>
  );
}
